/**
 * 校准种类目录：每一种校准"发哪条命令、值放在哪个参数位、结论从哪来"。
 *
 * ## 一、参数序号**不写死**，一律从 `@hgc/ardupilot` 的元数据反查
 *
 * `MAV_CMD_PREFLIGHT_CALIBRATION` 把 7 种校准挤在 `param1`…`param7` 里
 * （上游描述：*"only one sensor should be set in a single message and all others
 * should be zero"*）。把 `param2 = 1` 这样的分配抄进代码是最危险的一种硬编码：
 * 抄错一位的表现不是报错，而是**升级/降级固件后把另一个传感器校掉** ——
 * 用户以为在校罗盘，实际把加速度计的偏移覆盖了，直到第一次起飞才发现。
 *
 * 所以这里只记"这一项校的是上游哪个参数"，序号由
 * {@link paramIndexByLabel} 到 `CommandMeta.params` 的标签里反查。
 * 上游（或本仓库的中文标注）改了参数分配，下发位置自动跟着改；
 * 反查不到时**拒绝下发**（见 {@link resolveParamValue} 与 `manager.ts` 的 `param-not-found`），
 * 而不是退回 0 —— 退回 0 在飞控那边是"这项不校准"，然后多半回一个 `ACCEPTED`，
 * 于是地面站会兴高采烈地报"校准成功"，而实际上什么都没校。
 *
 * ## 二、参数值能用枚举就用枚举
 *
 * `param2 = 1` 里的 `1` 是 `PREFLIGHT_CALIBRATION_MAGNETOMETER_START`；
 * 加速度计的 `1/2` 是 `PREFLIGHT_CALIBRATION_ACCELEROMETER_FULL/TRIM`。
 * 这些都有名字，就从生成表里查名字。**只有上游没给枚举的那几个值才写字面量**
 * （陀螺/气压温度校准的 `1` 与 `3`，来自参数说明文本），每个都标了出处。
 */

import { commandId, commandOf, type CommandRef } from '@hgc/ardupilot';
import { enumValue } from '@hgc/mavlink';

/* ────────────────────────────── 种类 ────────────────────────────── */

/**
 * 校准种类。
 *
 * 名字刻意用"传感器 + 变体"的形式，而不是照抄上游的参数名：
 * 界面上的按钮是"校准罗盘"，不是"param2 = 1"。
 */
export type CalibrationKind =
  /** 磁力计（罗盘）校准：多罗盘、多 attempt，有真实进度与报告 */
  | 'mag'
  /** 已知航向的磁校准：用已知航向 + 磁偏角表直接算偏移，不用转机体 */
  | 'mag-yaw'
  /** 加速度计六面校准：飞控逐个方向指挥，用户在中间必须真的搬动飞机 */
  | 'accel'
  /** 水平校准（加速度计配平）：把当前姿态当作水平 */
  | 'accel-level'
  | 'gyro'
  | 'gyro-temp'
  /** 气压计地面校准 */
  | 'baro'
  | 'baro-temp'
  | 'rc'
  /** 认识但本向导不支持的种类（原因见 {@link CalibrationKindInfo.unsupportedReason}） */
  | 'esc'
  | 'compassmot'
  | 'airspeed';

/** 结论从哪里来 —— 决定"命令被接受"之后还要不要继续等。 */
export type CalibrationConclusionSource =
  /** 命令只是"开始"，结论必须等 `MAG_CAL_REPORT`（每个罗盘一份） */
  | 'mag-report'
  /** 飞控用 `MAV_CMD_ACCELCAL_VEHICLE_POS` 逐个方向指挥，结论也从它来 */
  | 'accel-positions'
  /** 没有过程遥测：命令的 `COMMAND_ACK` 就是结论（失败原因可能在 STATUSTEXT 里） */
  | 'command-ack';

/* ────────────────────────────── 参数位 ────────────────────────────── */

/**
 * 上游 `MAV_CMD_PREFLIGHT_CALIBRATION` 的每一项写在哪个参数位。
 *
 * 键是我们内部的用途，值是**该参数在上游/本地元数据里的标签**——
 * 序号靠它反查（`param1` 是"陀螺温度校准"、`param5` 是"加速度计校准"……）。
 * 注意 `param1` 同时管陀螺校准（值 1）与陀螺温度校准（值 3），
 * `param7` 同时管电调校准（值 1）与气压温度校准（值 3），上游就是这么定义的。
 */
export const PREFLIGHT_CALIBRATION_PARAMS = {
  gyro: '陀螺温度校准',
  mag: '磁力计校准',
  baro: '地面气压校准',
  rc: '遥控校准',
  accel: '加速度计校准',
  compassmot: '罗盘或空速校准',
  esc: '电调或气压校准',
} as const;

export type PreflightCalibrationParam = keyof typeof PREFLIGHT_CALIBRATION_PARAMS;

export const MAG_CAL_PARAMS = {
  /** 要校准的磁力计位掩码（0 = 全部能启动的） */
  mask: '磁力计掩码',
  retry: '失败重试',
  autosave: '自动保存',
  delay: '延时',
  autoreboot: '自动重启',
} as const;

export const MAG_YAW_PARAMS = {
  yaw: '航向',
  mask: '罗盘掩码',
  lat: '纬度',
  lon: '经度',
} as const;

export const ACCELCAL_POSITION_PARAMS = { position: '姿态' } as const;

/** 参数值的来源。能查枚举就查枚举，见文件头第二条。 */
export type CalibrationParamValue =
  | number
  | { readonly enumName: string; readonly entryName: string };

/** 参数引用：命令 + 参数标签 + 值。`manager.ts` 用它组 `params` 数组。 */
export interface CalibrationParamRef {
  readonly command: string;
  readonly label: string;
  readonly value: CalibrationParamValue;
}

/** 按元数据里的标签反查 1..7 的参数序号；查不到返回 `null`（调用方必须拒绝下发）。 */
export function paramIndexByLabel(command: CommandRef, label: string): number | null {
  const meta = commandOf(command);
  if (meta === undefined) return null;
  for (const param of meta.params) {
    if (param.label === label) return param.index;
  }
  return null;
}

/**
 * 参数值 → 数字。
 *
 * 枚举查不到时返回 `null`（而不是 0）：0 在飞控那边表示"这项不校准"，
 * 悄悄退化成 0 会让一次校准变成一次空转 + 一条"成功"。
 */
export function resolveParamValue(value: CalibrationParamValue): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  return enumValue(value.enumName, value.entryName);
}

/** 参数引用 → 命令编号；命令不在这份协议表里时 `null`。 */
export function commandNumberOf(command: CommandRef): number | null {
  return commandId(command) ?? null;
}

/* ────────────────────────────── 目录 ────────────────────────────── */

export interface CalibrationKindInfo {
  readonly kind: CalibrationKind;
  /** 界面按钮上的名字 */
  readonly label: string;
  /** 一句话说明这一步在做什么 */
  readonly summary: string;
  readonly supported: boolean;
  /** `supported === false` 时非空：**如实告诉用户为什么不做**，不要假装执行 */
  readonly unsupportedReason: string | null;
  /** 是否需要用户转动/搬动机体（界面据此显示操作动画与文案） */
  readonly requiresRotation: boolean;
  readonly conclusion: CalibrationConclusionSource;
  /** 绝对超时（毫秒）：到点给结论，绝不让进度条永远转下去 */
  readonly timeoutMs: number;
  /** 进度看门狗：这么久没有任何进度帧就判"中断"。`null` 表示该种类本来就没有过程遥测 */
  readonly stallTimeoutMs: number | null;
  /** 开始前必须做到的事（界面照实显示，尽量写"为什么"） */
  readonly preparation: readonly string[];
  /** 提示用的危险说明 */
  readonly riskNote: string | null;
  /** 做完之后要回读并展示的参数名（磁力计按罗盘序号在运行时生成） */
  readonly readbackParams: readonly string[];
  /** 怎么启动它。`null` 表示不支持（不应下发任何帧） */
  readonly launch: CalibrationParamRef | null;
  /** 磁力计族：结论来自 `MAG_CAL_REPORT`，因此需要 `DO_*_MAG_CAL` 的掩码语义 */
  readonly usesMagCalMask: boolean;
}

/**
 * 默认超时。
 *
 * 取值依据是真机上的观察值：磁力计校准通常 30~60 秒（开重试后可能两三分钟）；
 * 加速度计六面校准的时间几乎全花在用户搬飞机上，给 5 分钟；
 * 陀螺/气压这类几秒到几十秒的事给 1 分钟，遥控校准与温度校准再放宽。
 */
const TIMEOUT_MAG_MS = 3 * 60_000;
const TIMEOUT_MAG_YAW_MS = 30_000;
const TIMEOUT_ACCEL_MS = 5 * 60_000;
const TIMEOUT_SHORT_MS = 60_000;
/**
 * 进度看门狗窗口。
 *
 * 真机上 `MAG_CAL_PROGRESS` 是跟着 `MAG_CAL_REPORT` 一起流式推送的（EXTRA3 组，
 * 几赫兹）。15 秒收不到任何一帧，说明要么链路只下行不通，要么飞控那边的校准
 * 线程已经死了 —— 两种都不该让界面上那条进度条继续转。
 */
const STALL_MAG_MS = 15_000;

/** ArduPilot 罗盘参数的命名：第 1 个无后缀，第 2/3 个带 `2`/`3`（`COMPASS_OFS2_X`）。 */
export function compassParamNames(compassId: number): readonly string[] {
  const suffix = compassId === 0 ? '' : String(compassId + 1);
  return [
    `COMPASS_OFS${suffix}_X`,
    `COMPASS_OFS${suffix}_Y`,
    `COMPASS_OFS${suffix}_Z`,
    `COMPASS_DIA${suffix}_X`,
    `COMPASS_DIA${suffix}_Y`,
    `COMPASS_DIA${suffix}_Z`,
    `COMPASS_ODI${suffix}_X`,
    `COMPASS_ODI${suffix}_Y`,
    `COMPASS_ODI${suffix}_Z`,
  ];
}

/**
 * 磁力计合格判据参数。
 *
 * ArduPilot 用 `COMPASS_CAL_FIT` 作为"fitness 上限"，默认 16（`AP_Compass.h` 的
 * `AP_COMPASS_CALIBRATION_FITNESS_DEFAULT`）；内部罗盘在
 * `AP_Compass::start_calibration_all()` 里拿的是这个阈值的两倍
 * （注释原文：*internal compasses tend to be a lot noisier*）。
 * 我们**无法**从遥测里分辨一个罗盘是内部还是外部，所以判定一律按 1 倍阈值，
 * 并在文案里写清用的是哪个阈值 —— 宁可把"其实合格"说成"偏高"，也不能反过来。
 */
export const COMPASS_CAL_FIT_PARAM = 'COMPASS_CAL_FIT';

/**
 * 回读用的参数名。
 *
 * 这些名字没法像命令参数那样查表：ArduPilot 的参数名不在仓库里，也没有一个
 * "命令参数 → 参数名"的映射表（`apm.pdef.json` 只有名字与说明，没有"哪次校准写了哪几个"）。
 * 所以只能写在这里，并逐条注释它属于哪个库 —— 名字写错的表现是回读超时，
 * 不会静默显示错的数值（见 `manager.ts` 的 `readback`）。
 */
const ACCEL_RESULT_PARAMS: readonly string[] = [
  'INS_ACCOFFS_X',
  'INS_ACCOFFS_Y',
  'INS_ACCOFFS_Z',
  'INS_ACCSCAL_X',
  'INS_ACCSCAL_Y',
  'INS_ACCSCAL_Z',
];

const GYRO_RESULT_PARAMS: readonly string[] = [
  'INS_GYROFFS_X',
  'INS_GYROFFS_Y',
  'INS_GYROFFS_Z',
];

const BARO_RESULT_PARAMS: readonly string[] = ['GND_ABS_PRESS', 'GND_PRESS_OFFSET', 'GND_TEMP'];

const PREPARATION_MAG: readonly string[] = [
  '把机体搬到远离金属、磁铁、动力线与钢筋混凝土的地方（fitness 过大基本都是这个原因）',
  '按界面提示缓慢转动整架机体：各个方向都要转到，包括上下翻转',
  '校准时不要动遥控器油门、不要切模式 —— 飞控正在写罗盘偏移',
];

/**
 * 校准目录。**顺序即界面顺序**：常用、安全的在前。
 *
 * 不支持的三种也留在这里（而不是删掉）：界面需要能把"为什么没有电调校准"
 * 直接答出来，而不是让用户在空白菜单前猜。
 */
export const CALIBRATION_KINDS: readonly CalibrationKindInfo[] = Object.freeze([
  {
    kind: 'mag',
    label: '磁力计（罗盘）校准',
    summary: '转动整架机体，让飞控拟合出罗盘偏移与缩放',
    supported: true,
    unsupportedReason: null,
    requiresRotation: true,
    conclusion: 'mag-report',
    timeoutMs: TIMEOUT_MAG_MS,
    stallTimeoutMs: STALL_MAG_MS,
    preparation: PREPARATION_MAG,
    riskNote: null,
    readbackParams: [COMPASS_CAL_FIT_PARAM],
    launch: {
      command: 'MAV_CMD_DO_START_MAG_CAL',
      // 掩码在运行时按用户选的罗盘算（见 manager.ts）；这里只声明参数位
      label: MAG_CAL_PARAMS.mask,
      value: 0,
    },
    usesMagCalMask: true,
  },
  {
    kind: 'accel',
    label: '加速度计六面校准',
    summary: '按提示把机体依次摆成 6 个方向，飞控逐个方向取样',
    supported: true,
    unsupportedReason: null,
    requiresRotation: true,
    conclusion: 'accel-positions',
    timeoutMs: TIMEOUT_ACCEL_MS,
    stallTimeoutMs: null,
    preparation: [
      '把机体放在水平的平面上（有条件就用水平仪，平面歪了校准就白做）',
      '按提示把机体依次摆成 6 个方向：水平、左侧、右侧、机头朝下、机头朝上、背面朝下',
      '每个方向摆好后先**静止不动**再点"已摆好" —— 飞控是在你确认之后才取样的',
    ],
    riskNote: null,
    readbackParams: ACCEL_RESULT_PARAMS,
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.accel,
      value: {
        enumName: 'PREFLIGHT_CALIBRATION_ACCELEROMETER',
        entryName: 'PREFLIGHT_CALIBRATION_ACCELEROMETER_FULL',
      },
    },
    usesMagCalMask: false,
  },
  {
    kind: 'accel-level',
    label: '水平校准（加速度计配平）',
    summary: '把机体当前的姿态当作水平，只改配平量、不重新测六面',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: [
      '用水平仪把机体垫平（这一步的假设就是"现在它是水平的"）',
      '确认机体没有明显俯仰/横滚倾斜，否则校准会把倾斜当成水平',
    ],
    riskNote: null,
    readbackParams: ACCEL_RESULT_PARAMS,
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.accel,
      value: {
        enumName: 'PREFLIGHT_CALIBRATION_ACCELEROMETER',
        entryName: 'PREFLIGHT_CALIBRATION_ACCELEROMETER_TRIM',
      },
    },
    usesMagCalMask: false,
  },
  {
    kind: 'gyro',
    label: '陀螺仪校准',
    summary: '机体重置静止数秒，飞控测出陀螺零偏',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: ['机体重置静止，过程中不要碰它、不要让风吹动'],
    riskNote: null,
    readbackParams: GYRO_RESULT_PARAMS,
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.gyro,
      // 上游参数说明原文："1: gyro calibration, 3: gyro temperature calibration"。
      // 这两个值上游没有对应的枚举条目，只能照说明文本写。
      value: 1,
    },
    usesMagCalMask: false,
  },
  {
    kind: 'gyro-temp',
    label: '陀螺温度校准',
    summary: '飞控加热陀螺并记录温度-零偏曲线（耗时较长，且会明显发热）',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS * 5,
    stallTimeoutMs: null,
    preparation: ['机体保持静止、散热良好、电池电量充足（加热很耗电）'],
    riskNote: '温度校准会让飞控发热：确保机体周围通风，且电量足够跑完整个流程。',
    readbackParams: [],
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.gyro,
      // 上游说明原文："3: gyro temperature calibration"
      value: 3,
    },
    usesMagCalMask: false,
  },
  {
    kind: 'baro',
    label: '气压计校准（地面气压）',
    summary: '把当前气压记作地面气压',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: ['机体静止、舱盖关好，不要让桨叶转动或风吹着气压计'],
    riskNote: null,
    readbackParams: BARO_RESULT_PARAMS,
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.baro,
      value: { enumName: 'MAV_BOOL', entryName: 'MAV_BOOL_TRUE' },
    },
    usesMagCalMask: false,
  },
  {
    kind: 'baro-temp',
    label: '气压温度校准',
    summary: '记录气压计温度补偿（耗时较长）',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS * 5,
    stallTimeoutMs: null,
    preparation: ['机体静止，环境温度保持稳定、不要开空调或风扇对着机体吹'],
    riskNote: null,
    readbackParams: BARO_RESULT_PARAMS,
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      // param7 同时是"电调校准"，值 3 才是气压温度校准
      label: PREFLIGHT_CALIBRATION_PARAMS.esc,
      // 上游说明原文："3: barometer temperature calibration"
      value: 3,
    },
    usesMagCalMask: false,
  },
  {
    kind: 'rc',
    label: '遥控器校准',
    summary: '按提示把各通道打到行程两端，飞控记录舵量范围',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS * 3,
    stallTimeoutMs: null,
    preparation: [
      '遥控器已开机、已与接收机对频，机体上的遥控输入读数正常',
      '按界面提示把每个摇杆/开关打到两端再回中（只打一部分会窄化行程）',
      '过程中不要解锁电机',
    ],
    riskNote: null,
    readbackParams: [],
    launch: {
      command: 'MAV_CMD_PREFLIGHT_CALIBRATION',
      label: PREFLIGHT_CALIBRATION_PARAMS.rc,
      // 上游说明原文："1: radio RC calibration, 2: RC trim calibration"
      value: 1,
    },
    usesMagCalMask: false,
  },
  {
    kind: 'mag-yaw',
    label: '已知航向磁校准',
    summary: '已知机头朝向时直接算出罗盘偏移，不用转机体',
    supported: true,
    unsupportedReason: null,
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_MAG_YAW_MS,
    stallTimeoutMs: null,
    preparation: [
      '把机头对着已知航向（例如用指南针对正北），并把真实航向填进界面',
      '经纬度留空时飞控会用当前位置（需要 GPS 定位已就绪）',
    ],
    riskNote: null,
    readbackParams: [COMPASS_CAL_FIT_PARAM],
    launch: {
      command: 'MAV_CMD_FIXED_MAG_CAL_YAW',
      label: MAG_YAW_PARAMS.yaw,
      value: 0,
    },
    usesMagCalMask: true,
  },
  /* ── 认识但本向导不做：如实说明，不下发任何帧 ── */
  {
    kind: 'airspeed',
    label: '空速校准',
    summary: '（不支持）需要用户提供参考空速并另行写入空速参数',
    supported: false,
    unsupportedReason:
      '空速校准要求先给出一个可信的参考空速，之后还要写入 ARSPD_RATIO / ARSPD_OFFSET 之类的参数；只发一条命令既没法保证量程正确，也无法验证结果，所以本向导不做这一步。',
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: [],
    riskNote: null,
    readbackParams: [],
    launch: null,
    usesMagCalMask: false,
  },
  {
    kind: 'compassmot',
    label: '罗盘/电机干扰校准',
    summary: '（不支持）需要解锁并推油门让电机转起来',
    supported: false,
    unsupportedReason:
      '这个校准要解锁并推油门让电机转起来（桨必须拆掉），属于高风险流程；地面站无法替你确认桨是否已拆，因此不在向导里提供，请用厂家工具或命令行完成。',
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: [],
    riskNote: null,
    readbackParams: [],
    launch: null,
    usesMagCalMask: false,
  },
  {
    kind: 'esc',
    label: '电调校准',
    summary: '（不支持）依赖上电时序与遥控油门配合',
    supported: false,
    unsupportedReason:
      '电调校准依赖"先给电调上电、再把油门推到顶"这个物理时序，推错会让电机立即起转；它不是一个能在界面上点一下就走完的流程，所以本向导不做。',
    requiresRotation: false,
    conclusion: 'command-ack',
    timeoutMs: TIMEOUT_SHORT_MS,
    stallTimeoutMs: null,
    preparation: [],
    riskNote: null,
    readbackParams: [],
    launch: null,
    usesMagCalMask: false,
  },
]);

const BY_KIND = new Map<CalibrationKind, CalibrationKindInfo>(
  CALIBRATION_KINDS.map((info) => [info.kind, info]),
);

/** 取某个种类的目录项；名字不认识时 `null`（界面应回"不支持这个种类"）。 */
export function calibrationKindInfo(kind: string): CalibrationKindInfo | null {
  return BY_KIND.get(kind as CalibrationKind) ?? null;
}

/** 全部种类（含不支持的那些）。 */
export function allCalibrationKinds(): readonly CalibrationKindInfo[] {
  return CALIBRATION_KINDS;
}
